<template>
  <div
    ref="root"
    class="app-dialer-common-popup"
    data-popup
    :style="componentStyle"
  >
    <div
      class="header"
      data-popup
    >
      <div
        class="title"
        data-popup
      >
        {{ title }}
      </div>

      <div
        class="icon-button"
        :class="{ 'is-disabled': isDisabled }"
        data-action="close"
        @click="onClose"
      >
        <or-icon icon="close" />
      </div>
    </div>

    <div
      class="content"
      :class="{ 'is-loading': isLoading }"
    >
      <or-loader
        v-if="isLoading"
        type="circle"
        is-loading
      />

      <slot
        v-if="!isLoading"
        name="default"
      />
    </div>
  </div>
</template>

<script lang="ts">
import { defineComponent } from 'vue-demi';
import {
  OrIconV3 as OrIcon,
  OrLoaderV3 as OrLoader,
} from '@onereach/ui-components-25';
import { useComponentReposition } from '@/composables/use-component-reposition';

export default defineComponent({
  name: 'AppDialerCommonPopup',
  components: {
    OrIcon,
    OrLoader,
  },
  props: {
    title: {
      type: String,
      default: '',
    },
    isLoading: Boolean,
    isDisabled: Boolean,
  },
  emits: ['close'],
  setup() {
    const { applyReposition, componentStyle } = useComponentReposition();
    return { applyReposition, componentStyle };
  },
  mounted() {
    const root = this.$refs.root as HTMLElement;
    this.applyReposition({
      component: root,
      container: this.getContainingBlock(),
      isMoveEventAvailable: (e: MouseEvent) => {
        return (e.target as HTMLDivElement).hasAttribute('data-popup');
      },
    });
  },
  methods: {
    onClose() {
      if (!this.isDisabled) {
        this.$emit('close');
      }
    },

    getContainingBlock() {
      return document.querySelector('.agent-ui-desktop') as HTMLElement;
    },
  },
});
</script>

<style lang="scss" scoped>
@use "@/styles" as *;
@use '../../styles' as *;

.app-dialer-common-popup {
  @include app-dialer-popup;

  display: inline-flex;
  flex-direction: column;
  max-height: 31.25rem;
  max-width: 37.5rem;
  cursor: grab;

  .content {
    flex: 1 1 auto;
    color: var(--or-c-on-surface);
    overflow-x: hidden;
    overflow-y: auto;
    cursor: auto;

    &.is-loading {
      display: flex;
      align-items: center;
      justify-content: center;
      min-height: 5rem;
    }
  }

  .icon-button {
    @include app-dialer-icon-button;
  }

  .header {
    display: flex;
    flex: 0 0 auto;
    align-items: center;
    margin: var(--s-5);

    .title {
      @include text-overflow;

      line-height: 1.5;
      color: var(--or-c-on-background);
      font-size: 1rem;
      font-weight: var(--fw-2);
    }

    [data-action='close'] {
      margin-left: auto;
    }
  }
}
</style>
